import ChevronRight from "@mui/icons-material/ChevronRight";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { amber, red } from "@mui/material/colors";
import { encampBrandColors } from "colors";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import { SATELLITE_MOVE_WINDOW_DAYS } from "encamp-shared/src/constants/wasteAccumulation";
import {
  AccumulationAreaListItemFragment,
  VolumeStatus,
  WasteAccumulationAreaType,
} from "generated-graphql/graphql";
import pluralize from "pluralize";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { formatDaysRemaining } from "routes/Customer/Waste/Containers/containerDisplay";
import { formatDateLikeForDisplay } from "util/dates";

import { displayAreaName } from "./areaDisplay";

const NO_VALUE_PLACEHOLDER = "";
/** A blank reads as broken as one half of a pair, and a zero would be a
 * false assertion — see the note on `acuteKg`. */
const UNKNOWN_PAIR_HALF = "—";
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** A `null` threshold means the facility is exempt from that cap (VSQG), not
 * a zero allowance — so this copy replaces the number wherever a threshold
 * would otherwise render. Today a VSQG is the only way `exempt` can be true;
 * if a future ruleset ever exempts another status, the API should send the
 * reason alongside the flag rather than this copy guessing at it. */
const NO_LIMIT_COPY = "No limit (VSQG exempt)";

/** What to show in place of a null threshold: the exemption if the API
 * asserted one, otherwise the neutral placeholder. */
function noLimitLabel(exempt: boolean): string {
  return exempt ? NO_LIMIT_COPY : NO_VALUE_PLACEHOLDER;
}

/** The list query's row shape. */
export type AreaCardArea = AccumulationAreaListItemFragment;

/**
 * A gallon figure at no more than two decimal places, with trailing zeros
 * dropped so a whole number stays whole.
 *
 * Volumes are summed across a mixed set of container fills, so binary floating
 * point routinely lands on values like 266.99999999999994. Rounding at the point
 * of display rather than in the rollup keeps the stored total exact.
 */
export function formatGallons(value: number): string {
  return String(Math.round(value * 100) / 100);
}

/** "Jul 1, 2026", or the em-dash placeholder when the date is unset. */
function formatAreaDate(date: string | null | undefined): string {
  if (!date) {
    return NO_VALUE_PLACEHOLDER;
  }
  return formatDateLikeForDisplay(date, { format: "MMM d, yyyy" }) || NO_VALUE_PLACEHOLDER;
}

export const STATUS_COLOR: Record<VolumeStatus, "success" | "warning" | "error"> = {
  [VolumeStatus.Ok]: "success",
  [VolumeStatus.Approaching]: "warning",
  [VolumeStatus.Over]: "error",
  // Unproven, not bad news: the totals skipped waste they couldn't evaluate.
  [VolumeStatus.Undetermined]: "warning",
};

export const STATUS_LABEL: Record<VolumeStatus, string> = {
  [VolumeStatus.Ok]: "Room to accumulate",
  [VolumeStatus.Approaching]: "Approaching limit",
  [VolumeStatus.Over]: "Over threshold",
  [VolumeStatus.Undetermined]: "Can't determine",
};

/**
 * The same statuses, worded for a satellite area.
 *
 * A satellite area over its limit has one remedy — move the waste to central
 * accumulation, and within three days — so the label says that instead of just
 * naming the state. Deliberately NOT folded into `STATUS_LABEL`: that map is
 * also read for central areas, where "move to central" is nonsense.
 */
export const SATELLITE_STATUS_LABEL: Record<VolumeStatus, string> = {
  ...STATUS_LABEL,
  [VolumeStatus.Over]: "At capacity — move to central",
};

/**
 * The gauge/chip color per volume status, as specced in Figma.
 *
 * These are the design's own picks rather than the theme's `success`/`warning`/
 * `error` semantics, which run darker and read as alerting on a card that is
 * mostly a fill gauge. Two come from the Material scale the design system
 * already draws from; the lime is the brand's, so it resolves through
 * `encampBrandColors` instead of being pasted in as a hex.
 */
const VOLUME_STATUS_FILL: Record<VolumeStatus, string> = {
  [VolumeStatus.Ok]: encampBrandColors.yellowGreen, // Accent Lime #8DC63F
  [VolumeStatus.Approaching]: amber[500],
  [VolumeStatus.Over]: red[300],
  // An undetermined gauge shows a warning fill: the number drawn is a floor,
  // not the real total.
  [VolumeStatus.Undetermined]: amber[500],
};

/** Layout for the card's status pills; the color comes from `StatusChip`'s
 * semantic role rather than from anything set here. */
const CARD_CHIP_SX = { alignSelf: "flex-start", maxWidth: "100%" } as const;

/** Severity levels for the central nearest-deadline clockpill. */
type DeadlineLevel = "neutral" | "warning" | "error";

/**
 * Clockpill role per level. Neutral takes `success` rather than a grey: a
 * deadline comfortably far out is good news, not an absence of news.
 *
 * These are the theme's semantic roles, not the gauge's Figma colors. The
 * gauge fill stays on the canvas's own `amber/500`, `red/300` and Accent Lime
 * (see `VOLUME_STATUS_FILL`) because a progress bar is not a chip; the chips
 * follow the design system's chip spec, which owns them.
 */
const CLOCKPILL_COLOR: Record<DeadlineLevel, StatusChipColor> = {
  neutral: "success",
  warning: "warning",
  error: "error",
};

/**
 * Days remaining until `deadline`, rounded up so "later today" still reads as 1.
 * Goes negative once the deadline has passed: a missed move window is a worse
 * fact than an imminent one, and clamping it to zero would report the two the
 * same way.
 */
function daysLeft(deadline: string, now: Date): number {
  const diffMs = new Date(deadline).getTime() - now.getTime();
  return Math.ceil(diffMs / MS_PER_DAY);
}

/** Semantic level for the central nearest-deadline pill as the deadline nears. */
function deadlineLevel(date: string | null | undefined, now: Date): DeadlineLevel {
  if (!date) {
    return "neutral";
  }
  const days = daysLeft(date, now);
  if (days <= 7) {
    return "error";
  }
  if (days <= 30) {
    return "warning";
  }
  return "neutral";
}

/** A small filled circle in a given theme color (an sx-resolvable color path,
 * e.g. "warning.main"), for status lines / pills. */
function Dot({ color, size = 7 }: { color: string; size?: number }) {
  return (
    <Box sx={{ width: size, height: size, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
  );
}

/**
 * The bordered pill gauge from the prototype. A styled `LinearProgress` so it
 * keeps `role="progressbar"` + aria-valuenow/min/max and an aria-label for
 * free; only the visual rendering changes. The track is the theme's standard
 * "resting surface" token with a hairline divider border; the fill is a
 * theme semantic color path (e.g. "success.main") resolved via `sx`.
 */
function Gauge({
  value,
  fill,
  ariaLabel,
  testId,
}: {
  value: number;
  fill: string;
  ariaLabel: string;
  testId?: string;
}) {
  return (
    <LinearProgress
      variant="determinate"
      value={value}
      aria-label={ariaLabel}
      data-testid={testId}
      sx={{
        height: "0.75rem",
        borderRadius: "99px",
        bgcolor: "action.hover",
        border: "1px solid",
        borderColor: "divider",
        "& .MuiLinearProgress-bar": { backgroundColor: fill, borderRadius: "99px" },
      }}
    />
  );
}

/** The "Satellite" / "Central" type chip pinned to the card header. A plain
 * outlined pill for both types: the chip names which kind of area this is, and
 * the mockup keeps that neutral so the only tinted thing on a card is its
 * status — where color actually carries meaning. */
function TypeChip({ type }: { type: "Satellite" | "Central" }) {
  return <Chip label={type} size="small" variant="outlined" sx={{ flexShrink: 0 }} />;
}

/**
 * The satellite card's status pill: the volume status, tinted by severity.
 *
 * Replaces the dot-plus-text line the card used to carry, and absorbs the
 * separate move-deadline alert that sat beneath it — an over-capacity area now
 * states the remedy and the countdown in one pill, so the countdown isn't lost
 * along with the alert box.
 */
function SatelliteStatusChip({
  status,
  daysRemaining,
}: {
  status: VolumeStatus;
  /** Days until the move deadline, negative once passed; omitted when the area
   * has no deadline (it is not over) or the API has not set one. */
  daysRemaining?: number;
}) {
  const label =
    status === VolumeStatus.Over && daysRemaining != null
      ? `${SATELLITE_STATUS_LABEL[status]} · ${formatDaysRemaining(daysRemaining)}`
      : SATELLITE_STATUS_LABEL[status];

  return (
    <StatusChip
      label={label}
      size="small"
      color={STATUS_COLOR[status]}
      variant="soft"
      data-testid="satellite-status"
      sx={CARD_CHIP_SX}
    />
  );
}

/**
 * The progress bars + status copy shared by the card and the area detail
 * page's volume section. Takes only the volume shape (not the whole area) so
 * a detail-page query selecting a different row type still fits.
 *
 * `variant="compact"` (the default, used by `AreaCard`) shows the non-acute
 * bar plus a numeric-only acute row. `variant="detailed"` (the area detail
 * page) shows the acute component as its own second bar instead, since the
 * spec wants both volume components visible as thresholded gauges there.
 */
/**
 * Everything the gauge needs to decide, derived once from the volume shape.
 *
 * Kept apart from the markup because the decisions are where the subtlety is —
 * which thresholds exist, what "over" actually cites, whether a component is
 * capped or exempt — and none of it depends on how the result is drawn.
 */
function satelliteGaugeModel(volume: AreaCardArea["volume"], variant: "compact" | "detailed") {
  const { nonAcute, acute, status: volumeStatus, moveDeadline, exempt } = volume;
  const threshold = nonAcute.threshold;
  const hasNonAcuteThreshold = threshold != null;
  const percent =
    hasNonAcuteThreshold && threshold > 0 ? Math.round((nonAcute.total / threshold) * 100) : 0;

  const hasAcuteVolume = acute.totalVolume > 0 || (acute.totalKg ?? 0) > 0;
  const acuteVolumeThreshold = acute.volumeThreshold;
  const acuteKgThreshold = acute.kgThreshold;
  // The API derives both acute caps from one limit object, so they are null
  // together; requiring both keeps a half-set pair from rendering as capped.
  const hasAcuteThreshold = acuteVolumeThreshold != null && acuteKgThreshold != null;
  const acutePercent =
    hasAcuteThreshold && acuteVolumeThreshold > 0
      ? Math.round((acute.totalVolume / acuteVolumeThreshold) * 100)
      : 0;
  const showAcuteDetails = variant === "detailed" && hasAcuteVolume;
  // A null cached acute mass is unknown, not zero: `addToTotals` only adds to
  // it when the unit converts to kg without a density, so an acute container
  // recorded in gallons against a stream with no density leaves it null. The
  // row only renders behind `hasAcuteVolume`, so a null here always means
  // acute waste is present and its mass is not derivable — never that the
  // area is empty.
  const acuteKg = acute.totalKg ?? UNKNOWN_PAIR_HALF;
  const acuteAmounts = hasAcuteThreshold
    ? `${acute.totalVolume} / ${acuteVolumeThreshold} gal · ${acuteKg} / ${acuteKgThreshold} kg`
    : `${acute.totalVolume} gal · ${acuteKg} kg${exempt ? ` — ${NO_LIMIT_COPY}` : ""}`;

  return {
    nonAcute,
    acute,
    volumeStatus,
    moveDeadline,
    exempt,
    threshold,
    hasNonAcuteThreshold,
    percent,
    progressValue: Math.min(100, percent),
    nonAcuteColor: VOLUME_STATUS_FILL[nonAcute.status],
    acuteColor: VOLUME_STATUS_FILL[acute.status],
    // The dot-and-label line is detail-page-only, where the theme semantics
    // still read correctly against body copy; the card uses the pill instead.
    statusColor: `${STATUS_COLOR[volumeStatus]}.main`,
    acutePercent,
    acuteProgressValue: Math.min(100, acutePercent),
    showAcuteRow: variant === "compact" && hasAcuteVolume,
    showAcuteDetails,
    showAcuteBar: showAcuteDetails && hasAcuteThreshold,
    acuteAmounts,
    overDescription: overThresholdDescription(volume),
    daysRemaining: moveDeadline ? daysLeft(moveDeadline, new Date()) : 0,
  };
}

/**
 * What the over-threshold alert actually cites.
 *
 * The area's status is the worst of its components, so naming the non-acute
 * 55-gal limit unconditionally is wrong when only the acute component tripped.
 * A component with no threshold (VSQG-exempt) can never legitimately be OVER,
 * so its string is only ever built when a real threshold exists.
 */
function overThresholdDescription(volume: AreaCardArea["volume"]): string {
  const { nonAcute, acute } = volume;
  const overParts: string[] = [];
  if (nonAcute.status === VolumeStatus.Over && nonAcute.threshold != null) {
    overParts.push(`Over ${nonAcute.threshold} gal`);
  }
  if (
    acute.status === VolumeStatus.Over &&
    acute.volumeThreshold != null &&
    acute.kgThreshold != null
  ) {
    overParts.push(`Over the acute limit (${acute.volumeThreshold} gal / ${acute.kgThreshold} kg)`);
  }
  return overParts.length > 0 ? overParts.join(" and ") : "Over threshold";
}

/** Why the volume verdict is unproven, and how much of the area the gauge is blind to. */
function undeterminedDescription(undeterminedContainerCount: number): string {
  return (
    `Can't check the volume limit — ${undeterminedContainerCount} ` +
    `${pluralize("container", undeterminedContainerCount)} recorded by weight with no specific ` +
    "gravity on the waste stream profile. The total shown is only the waste that could be counted."
  );
}

/** The non-acute total over its limit, and the bar that reads it back. */
function NonAcuteGauge({
  model,
  areaName,
}: {
  model: ReturnType<typeof satelliteGaugeModel>;
  areaName: string;
}) {
  const { nonAcute, threshold, hasNonAcuteThreshold, percent, exempt } = model;

  return (
    // The amounts and the bar they describe are one unit, so they sit in their
    // own tight stack rather than taking the card's wider element gap.
    <Stack spacing={0.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography component="div">
          <Box
            component="span"
            sx={{ fontSize: "1.25rem", fontWeight: 700, color: "text.primary" }}
          >
            {formatGallons(nonAcute.total)}
          </Box>{" "}
          <Box
            component="span"
            sx={{ typography: "body2", fontWeight: 600, color: "text.secondary" }}
          >
            {hasNonAcuteThreshold ? `/ ${threshold} gal` : `gal · ${noLimitLabel(exempt)}`}
          </Box>
        </Typography>
        {hasNonAcuteThreshold && (
          <Box
            component="span"
            sx={{ typography: "caption", fontWeight: 800, color: "text.secondary" }}
          >
            {percent}%
          </Box>
        )}
      </Stack>
      {hasNonAcuteThreshold && (
        <Gauge
          value={model.progressValue}
          fill={model.nonAcuteColor}
          ariaLabel={`${areaName} non-acute volume ${percent}% of threshold`}
        />
      )}
    </Stack>
  );
}

/**
 * The acute component, which the two variants state differently: the card has
 * room only for the numbers, the detail page draws them as a second gauge.
 */
function AcuteVolumeSection({
  model,
  areaName,
}: {
  model: ReturnType<typeof satelliteGaugeModel>;
  areaName: string;
}) {
  const { acuteColor, acuteAmounts, showAcuteRow, showAcuteDetails, showAcuteBar } = model;

  if (showAcuteRow) {
    return (
      <Typography variant="body2" sx={{ color: acuteColor }} data-testid="acute-row">
        {acuteAmounts}
      </Typography>
    );
  }
  if (!showAcuteDetails) {
    return null;
  }
  return (
    <>
      {showAcuteBar && (
        <Gauge
          value={model.acuteProgressValue}
          fill={acuteColor}
          ariaLabel={`${areaName} acute volume ${model.acutePercent}% of threshold`}
          testId="acute-bar"
        />
      )}
      <Typography variant="body2" sx={{ color: acuteColor }} data-testid="acute-caption">
        {acuteAmounts}
      </Typography>
    </>
  );
}

export function SatelliteVolumeGauge({
  volume,
  areaName,
  variant = "compact",
}: {
  volume: AreaCardArea["volume"];
  areaName: string;
  variant?: "compact" | "detailed";
}) {
  const model = satelliteGaugeModel(volume, variant);
  const { volumeStatus, moveDeadline, statusColor, daysRemaining } = model;

  return (
    <>
      <NonAcuteGauge model={model} areaName={areaName} />
      {/* The card states its status as a tinted pill (with the move countdown
          folded in); the detail page keeps the dot-and-label line, since it
          also carries the fuller alert below. */}
      {variant === "compact" ? (
        <SatelliteStatusChip
          status={volumeStatus}
          daysRemaining={moveDeadline ? daysRemaining : undefined}
        />
      ) : (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Dot color={statusColor} />
          <Box component="span" sx={{ typography: "caption", fontWeight: 600, color: statusColor }}>
            {SATELLITE_STATUS_LABEL[volumeStatus]}
          </Box>
        </Stack>
      )}
      <AcuteVolumeSection model={model} areaName={areaName} />
      {/* Detail-page only. On a card the status pill above already carries the
          remedy and the countdown, and the mockup has no alert box there. */}
      {variant === "detailed" && volumeStatus === VolumeStatus.Over && moveDeadline && (
        <Alert
          severity="error"
          icon={<WarningAmberRounded sx={{ fontSize: "1rem" }} />}
          sx={{ typography: "caption", fontWeight: 700, borderRadius: "0.5625rem", py: 1 }}
        >
          {model.overDescription} — move to central within {SATELLITE_MOVE_WINDOW_DAYS}{" "}
          {pluralize("day", SATELLITE_MOVE_WINDOW_DAYS)} · {formatDaysRemaining(daysRemaining)}
        </Alert>
      )}
      {/* The gauge above drew only the evaluable gallons, so it reads as
          headroom; this says why that reading is unproven and what fixes it. */}
      {variant === "detailed" && volumeStatus === VolumeStatus.Undetermined && (
        <Alert
          severity="warning"
          icon={<WarningAmberRounded sx={{ fontSize: "1rem" }} />}
          sx={{ typography: "caption", fontWeight: 700, borderRadius: "0.5625rem", py: 1 }}
        >
          {undeterminedDescription(volume.undeterminedContainerCount)}
        </Alert>
      )}
    </>
  );
}

/**
 * Card header: bold area name over a muted location line, type chip right.
 *
 * Takes the stored name and strips the type prefix for display, so the card and
 * the detail page it opens show the same heading.
 */
function AreaCardHeader({
  name,
  location,
  type,
}: {
  name: string;
  location?: string | null;
  type: "Satellite" | "Central";
}) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
      <Box>
        <Typography component="div" variant="h6" sx={{ color: "text.primary", lineHeight: 1.2 }}>
          {displayAreaName(name)}
        </Typography>
        {location && (
          <Typography component="div" variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            {location}
          </Typography>
        )}
      </Box>
      <TypeChip type={type} />
    </Stack>
  );
}

/** A bold figure over a small grey label, used across the central card's row. */
function CardFigure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 72 }}>
      {children}
      <Typography
        component="div"
        variant="caption"
        sx={{ fontWeight: 600, color: "text.secondary" }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

/** Card surface shared by both variants: a soft grey tile with a hairline
 * border and a subtle elevation on hover, sitting inside the white section
 * card; over-threshold areas get the theme warning border.
 *
 * `data-copy-id` marks this card as the block representing one accumulation
 * area, so the palette's "Pick ID from page" mode can hover it the way it
 * hovers data grid rows. No type attribute: the cache already identifies the
 * id as a `WasteAccumulationArea`. */
function AreaCardShell({
  areaId,
  over,
  onClick,
  ariaLabel,
  children,
}: {
  areaId: string;
  over?: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
}) {
  return (
    <Card
      variant="outlined"
      data-copy-id={areaId}
      sx={{
        height: "100%",
        backgroundColor: (theme) => theme.palette.grey50.main,
        borderColor: over ? "warning.light" : "divider",
        transition: "box-shadow .15s, border-color .15s",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardActionArea
        component="div"
        onClick={onClick}
        aria-label={ariaLabel}
        sx={{ height: "100%" }}
      >
        {/* Padding unchanged; the gap between elements is a step wider than it
            was, which the tighter amounts-and-bar sub-stack pays for. */}
        <CardContent sx={{ p: 2 }}>
          <Stack spacing={2}>{children}</Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

/** The container count and the drill-in affordance shared by both card
 * variants' footers. The chevron is decorative — the whole card is already the
 * clickable target, so it carries no separate accessible name. */
function AreaCardFooter({ containerCount }: { containerCount: number }) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{
        typography: "caption",
        color: "text.secondary",
        borderTop: "1px solid",
        borderColor: "divider",
        pt: 1.25,
      }}
    >
      <span>{pluralize("container", containerCount, true)}</span>
      <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
    </Stack>
  );
}

function SatelliteAreaCard({ area, onClick }: { area: AreaCardArea; onClick: () => void }) {
  const isOver = area.volume.status === VolumeStatus.Over;

  return (
    <AreaCardShell areaId={area.id} over={isOver} onClick={onClick} ariaLabel={area.name}>
      <AreaCardHeader name={area.name} location={area.description} type="Satellite" />
      <SatelliteVolumeGauge volume={area.volume} areaName={area.name} />
      <AreaCardFooter containerCount={area.containerCount} />
    </AreaCardShell>
  );
}

/**
 * The central card's countdown pill — "Next deadline in 12 days", tinted as
 * the date nears.
 *
 * Relative rather than the absolute date the card used to show: the deadline
 * only matters as time remaining, and a date alone makes the reader do the
 * arithmetic. The absolute date stays available on hover (and on the area's
 * detail page) so nothing is actually lost.
 */
function NextDeadlineChip({ date }: { date: string | null | undefined }) {
  const now = new Date();
  const level = deadlineLevel(date, now);

  // No leading dot and no outline: this is the same pill as the satellite
  // status chip, so the two sections' cards read as one component.
  return (
    <StatusChip
      data-testid="nearest-deadline"
      size="small"
      color={CLOCKPILL_COLOR[level]}
      variant="soft"
      title={formatAreaDate(date)}
      label={date ? nextDeadlineLabel(daysLeft(date, now)) : ""}
      sx={CARD_CHIP_SX}
    />
  );
}

/** "Next deadline in 12 days", or "Deadline 3 days overdue" once it has passed.
 * Not `formatDaysRemaining`: that returns "12 days left", which reads as
 * "Next deadline in 12 days left" once prefixed. */
function nextDeadlineLabel(days: number): string {
  if (days < 0) {
    return `Deadline ${pluralize("day", -days, true)} overdue`;
  }
  return `Next deadline in ${pluralize("day", days, true)}`;
}

/** A bold figure for the central card's row. */
function CentralFigure({ label, value }: { label: string; value: string }) {
  return (
    <CardFigure label={label}>
      <Typography
        component="div"
        variant="h6"
        sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1 }}
      >
        {value}
      </Typography>
    </CardFigure>
  );
}

function CentralAreaCard({ area, onClick }: { area: AreaCardArea; onClick: () => void }) {
  const totalOnSite = area.volume.nonAcute.total + area.volume.acute.totalVolume;

  return (
    <AreaCardShell areaId={area.id} onClick={onClick} ariaLabel={area.name}>
      <AreaCardHeader name={area.name} location={area.description} type="Central" />
      {/* Only the volume figure here: the container count is in the footer, and
          repeating it as a figure would spend the card's most prominent row on
          a number the reader already has. The mockup's second figure —
          "deadlines within 60 days" — has no field behind it yet. */}
      <Stack direction="row" spacing={2.25} flexWrap="wrap" useFlexGap>
        <CentralFigure label="Gal on site" value={formatGallons(totalOnSite)} />
      </Stack>
      <NextDeadlineChip date={area.nearestDeadlineDate} />
      <AreaCardFooter containerCount={area.containerCount} />
    </AreaCardShell>
  );
}

export function AreaCard({ area }: { area: AreaCardArea }) {
  const navigate = useNavigate();
  const handleClick = () => navigate(`../accumulation-areas/${area.id}`);

  if (area.type === WasteAccumulationAreaType.Central) {
    return <CentralAreaCard area={area} onClick={handleClick} />;
  }
  return <SatelliteAreaCard area={area} onClick={handleClick} />;
}
